import React, { useState, useEffect } from "react";
import { fetchContent, updateContent } from "../../services/api";
import { useContent } from "../../context/ContentContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast";
import ImageUploader from "./ImageUploader";
import { Loader2, Plus, X, Save, LayoutTemplate } from "lucide-react";

const ICONS = ["Factory", "Globe2", "BadgeDollarSign", "Zap", "Ship", "ShieldCheck"];

const Section = ({ title, children, onAdd, addLabel }) => (
  <div className="bg-white rounded-xl border border-neutral-200 p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-display font-bold text-[#14171c]">{title}</h3>
      {onAdd && <button type="button" onClick={onAdd} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]"><Plus className="w-4 h-4" /> {addLabel}</button>}
    </div>
    {children}
  </div>
);

const AdminContent = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { refresh } = useContent();

  useEffect(() => { fetchContent().then(setC).catch(() => {}); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setArr = (key, i, field, v) => set(key, c[key].map((it, idx) => (idx === i ? { ...it, [field]: v } : it)));
  const removeArr = (key, i) => set(key, c[key].filter((_, idx) => idx !== i));
  // nested section object field: c[key][field]
  const setSec = (key, field, v) => set(key, { ...c[key], [field]: v });
  // nested section items array
  const setSecItem = (key, i, field, v) => set(key, { ...c[key], items: c[key].items.map((it, idx) => (idx === i ? { ...it, [field]: v } : it)) });
  const addSecItem = (key, blank) => set(key, { ...c[key], items: [...(c[key].items || []), blank] });
  const removeSecItem = (key, i) => set(key, { ...c[key], items: c[key].items.filter((_, idx) => idx !== i) });
  const setSecGallery = (key, i, v) => set(key, { ...c[key], gallery: c[key].gallery.map((it, idx) => (idx === i ? v : it)) });

  const save = async () => {
    setSaving(true);
    try {
      await updateContent({
        hero: c.hero, stats: c.stats, whyChooseUs: c.whyChooseUs,
        footerAbout: c.footerAbout, officesLine: c.officesLine,
        categoriesSection: c.categoriesSection, factorySection: c.factorySection,
        projectsSection: c.projectsSection, testimonialsSection: c.testimonialsSection,
      });
      await refresh();
      toast({ title: "Content saved" });
    } catch (e) { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (!c) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between sticky top-0 bg-neutral-50 py-2 z-10">
        <div className="flex items-center gap-2">
          <LayoutTemplate className="w-5 h-5 text-[#C9A227]" />
          <h2 className="font-display font-bold text-lg text-[#14171c]">Homepage Content</h2>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#C9A227] hover:bg-[#A6841B] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save All
        </Button>
      </div>

      {/* Hero slides */}
      <Section title="Hero Slides" addLabel="Add slide" onAdd={() => set("hero", [...c.hero, { title: "", highlight: "", subtitle: "", image: "" }])}>
        <div className="space-y-5">
          {c.hero.map((h, i) => (
            <div key={i} className="border border-neutral-200 rounded-lg p-4 relative">
              <button type="button" onClick={() => removeArr("hero", i)} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              <div className="text-xs font-bold text-neutral-400 mb-3">SLIDE {i + 1}</div>
              <ImageUploader label="Background Image" value={h.image} onChange={(v) => setArr("hero", i, "image", v)} />
              <div className="mt-3 space-y-3">
                <div><Label className="text-xs">Title</Label><Input className="mt-1" value={h.title} onChange={(e) => setArr("hero", i, "title", e.target.value)} /></div>
                <div><Label className="text-xs">Highlight (gold text)</Label><Input className="mt-1" value={h.highlight} onChange={(e) => setArr("hero", i, "highlight", e.target.value)} /></div>
                <div><Label className="text-xs">Subtitle</Label><Textarea className="mt-1" rows={2} value={h.subtitle} onChange={(e) => setArr("hero", i, "subtitle", e.target.value)} /></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Stats */}
      <Section title="Factory Stats">
        <div className="grid grid-cols-2 gap-3">
          {c.stats.map((s, i) => (
            <div key={i} className="flex gap-2">
              <Input value={s.value} onChange={(e) => setArr("stats", i, "value", e.target.value)} placeholder="5,000+" className="w-24" />
              <Input value={s.label} onChange={(e) => setArr("stats", i, "label", e.target.value)} placeholder="Label" />
            </div>
          ))}
        </div>
      </Section>

      {/* Why choose us */}
      <Section title="Why Choose Us Cards">
        <div className="space-y-3">
          {c.whyChooseUs.map((w, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[130px_1fr] gap-2 items-start border border-neutral-100 rounded-lg p-3">
              <select value={w.icon} onChange={(e) => setArr("whyChooseUs", i, "icon", e.target.value)} className="h-9 rounded-md border border-neutral-200 px-2 text-sm">
                {ICONS.map((ic) => (<option key={ic} value={ic}>{ic}</option>))}
              </select>
              <div className="space-y-2">
                <Input value={w.title} onChange={(e) => setArr("whyChooseUs", i, "title", e.target.value)} placeholder="Title" />
                <Textarea rows={2} value={w.text} onChange={(e) => setArr("whyChooseUs", i, "text", e.target.value)} placeholder="Text" />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Categories heading */}
      <Section title='"Explore Our Product Categories" Heading'>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Eyebrow</Label><Input className="mt-1" value={c.categoriesSection.eyebrow} onChange={(e) => setSec("categoriesSection", "eyebrow", e.target.value)} /></div>
          <div><Label className="text-xs">Title</Label><Input className="mt-1" value={c.categoriesSection.title} onChange={(e) => setSec("categoriesSection", "title", e.target.value)} /></div>
        </div>
        <p className="text-xs text-neutral-500 mt-2">Tip: manage the actual category cards in the <b>Categories</b> tab.</p>
      </Section>

      {/* Factory teaser */}
      <Section title='"Manufacturing Strength" Home Block'>
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Eyebrow</Label><Input className="mt-1" value={c.factorySection.eyebrow} onChange={(e) => setSec("factorySection", "eyebrow", e.target.value)} /></div>
            <div><Label className="text-xs">Title</Label><Input className="mt-1" value={c.factorySection.title} onChange={(e) => setSec("factorySection", "title", e.target.value)} /></div>
          </div>
          <div><Label className="text-xs">Text</Label><Textarea className="mt-1" rows={3} value={c.factorySection.text} onChange={(e) => setSec("factorySection", "text", e.target.value)} /></div>
          <Label className="text-xs">Images (4)</Label>
          <div className="grid grid-cols-2 gap-3">
            {(c.factorySection.gallery || []).map((img, i) => (
              <ImageUploader key={i} label="" value={img} onChange={(v) => setSecGallery("factorySection", i, v)} />
            ))}
          </div>
        </div>
      </Section>

      {/* Projects */}
      <Section title='"Successful Projects" Block' addLabel="Add project" onAdd={() => addSecItem("projectsSection", { title: "", category: "", image: "", desc: "" })}>
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div><Label className="text-xs">Eyebrow</Label><Input className="mt-1" value={c.projectsSection.eyebrow} onChange={(e) => setSec("projectsSection", "eyebrow", e.target.value)} /></div>
          <div><Label className="text-xs">Title</Label><Input className="mt-1" value={c.projectsSection.title} onChange={(e) => setSec("projectsSection", "title", e.target.value)} /></div>
        </div>
        <div className="space-y-4">
          {(c.projectsSection.items || []).map((p, i) => (
            <div key={i} className="border border-neutral-200 rounded-lg p-4 relative">
              <button type="button" onClick={() => removeSecItem("projectsSection", i)} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              <ImageUploader label="Image" value={p.image} onChange={(v) => setSecItem("projectsSection", i, "image", v)} />
              <div className="mt-3 grid md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Title</Label><Input className="mt-1" value={p.title} onChange={(e) => setSecItem("projectsSection", i, "title", e.target.value)} /></div>
                <div><Label className="text-xs">Category Tag</Label><Input className="mt-1" value={p.category} onChange={(e) => setSecItem("projectsSection", i, "category", e.target.value)} /></div>
              </div>
              <div className="mt-3"><Label className="text-xs">Description</Label><Textarea className="mt-1" rows={2} value={p.desc} onChange={(e) => setSecItem("projectsSection", i, "desc", e.target.value)} /></div>
            </div>
          ))}
        </div>
      </Section>

      {/* Testimonials */}
      <Section title='"What Our Clients Say" Block' addLabel="Add testimonial" onAdd={() => addSecItem("testimonialsSection", { name: "", role: "", text: "", rating: 5 })}>
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div><Label className="text-xs">Eyebrow</Label><Input className="mt-1" value={c.testimonialsSection.eyebrow} onChange={(e) => setSec("testimonialsSection", "eyebrow", e.target.value)} /></div>
          <div><Label className="text-xs">Title</Label><Input className="mt-1" value={c.testimonialsSection.title} onChange={(e) => setSec("testimonialsSection", "title", e.target.value)} /></div>
        </div>
        <div className="space-y-4">
          {(c.testimonialsSection.items || []).map((t, i) => (
            <div key={i} className="border border-neutral-200 rounded-lg p-4 relative">
              <button type="button" onClick={() => removeSecItem("testimonialsSection", i)} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              <div className="grid md:grid-cols-[1fr_1fr_80px] gap-3">
                <div><Label className="text-xs">Name</Label><Input className="mt-1" value={t.name} onChange={(e) => setSecItem("testimonialsSection", i, "name", e.target.value)} /></div>
                <div><Label className="text-xs">Role</Label><Input className="mt-1" value={t.role} onChange={(e) => setSecItem("testimonialsSection", i, "role", e.target.value)} /></div>
                <div><Label className="text-xs">Rating</Label><Input type="number" min="1" max="5" className="mt-1" value={t.rating} onChange={(e) => setSecItem("testimonialsSection", i, "rating", Number(e.target.value))} /></div>
              </div>
              <div className="mt-3"><Label className="text-xs">Quote</Label><Textarea className="mt-1" rows={2} value={t.text} onChange={(e) => setSecItem("testimonialsSection", i, "text", e.target.value)} /></div>
            </div>
          ))}
        </div>
      </Section>

      {/* Footer & header text */}
      <Section title="Footer & Header Text">
        <div className="space-y-4">
          <div><Label>Footer About Text</Label><Textarea className="mt-1.5" rows={3} value={c.footerAbout} onChange={(e) => set("footerAbout", e.target.value)} /></div>
          <div><Label>Header Offices Line</Label><Input className="mt-1.5" value={c.officesLine} onChange={(e) => set("officesLine", e.target.value)} placeholder="Hong Kong • United Kingdom • Spain" /></div>
        </div>
      </Section>

      <Button onClick={save} disabled={saving} className="bg-[#C9A227] hover:bg-[#A6841B] text-white">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save All Content
      </Button>
    </div>
  );
};

export default AdminContent;
